import {
  KITS,
  KIT_IDS,
  MATCH_MODES,
  colorDistance,
  contrastKitFor,
  isRewardKit,
  type MatchModeId,
  type PitchSkinId,
  type PlayerTalent,
  type SpawnSpec,
  type TeamInstructions,
  type TeamSide,
} from "@golazo/shared";
import type { StadiumDressing } from "../render/stadium.js";
import { nextFixture } from "./career.js";
import { DIVISIONS, type Career, type Club, type ClubStyle, type Foot, type Position } from "./model.js";
import type { MatchPerformance } from "./player.js";
import { hashSeed } from "./rng.js";
import { PROVOKED_SKILL, relationsOf, squadSkillOffset } from "./phoneTalk.js";
import { talentFromAttributes, talentFromClubRating } from "./talent.js";
import { TIER_RATING } from "./world.js";

/**
 * De la jornada de la carrera a un partido de verdad.
 *
 * Puro: dice QUE hay que montar (modo, equipos, estilo, estadio, talento, tu
 * sitio en el campo) y no toca nada. Quien lo aplica sobre el estado vivo del
 * juego —y lo deshace al acabar— es `game/careerMatch.ts`.
 *
 * Tu club juega en SU lado: de local a la izquierda y con tu gente en la
 * grada; de visitante a la derecha, en el campo del rival y con cuatro de los
 * tuyos en un rincon. El marcador se lee como una quiniela: el de casa primero.
 */
export interface CareerMatchSetup {
  season: number;
  matchday: number;
  tier: number;
  modeId: MatchModeId;
  /** El lado de TU club en el partido (el que juega en casa es `home`). */
  side: TeamSide;
  /** Tu asiento en tu equipo (`<side>-<seatIndex>`), segun posicion y pie. */
  seatIndex: number;
  /** `style`: como juega cada club (el rotulo del saque lo presenta). */
  teams: Record<TeamSide, { clubId: string; name: string; kitId: string; style: ClubStyle }>;
  instructions: Record<TeamSide, TeamInstructions>;
  pitchSkin: PitchSkinId;
  stadium: StadiumDressing;
  talent: { home: PlayerTalent; away: PlayerTalent; human: PlayerTalent };
  /** Nivel de la IA de cada equipo (ver `ai/skill.ts`): torpe en el barrio. */
  aiSkill: { home: number; away: number };
  seed: number;
  shirtNumber: number;
  nickname: string;
}

/**
 * Como juega cada estilo, en instrucciones de equipo. Es lo que manda el
 * mister: eres jugador, no entrenador, asi que tu plan de Personalizar no
 * entra aqui. Tu club juega como juega, te guste o no.
 */
export const STYLE_INSTRUCTIONS: Record<ClubStyle, TeamInstructions> = {
  // Autobus aparcado y fe.
  cerrojo: {
    mentality: -0.45,
    // Por debajo del umbral de la linea de presion: espera en su tercio.
    pressing: 0.15,
    width: 0.52,
    lineHeight: 0.3,
    lineLength: 0.8,
    compactness: 0.92,
    tempo: 0.5,
    lateRuns: 0.22,
    // Robado atras, fuera y lejos.
    directness: 0.65,
    outlet: 0,
  },
  // Correr hasta vomitar.
  presion: {
    mentality: 0.25,
    pressing: 0.92,
    width: 0.66,
    lineHeight: 0.76,
    lineLength: 0.72,
    compactness: 0.82,
    tempo: 0.72,
    lateRuns: 0.55,
    directness: 0.5,
    outlet: 0,
  },
  // Tres mil pases y un tiro.
  toque: {
    mentality: 0.15,
    pressing: 0.6,
    width: 0.76,
    lineHeight: 0.62,
    lineLength: 0.74,
    compactness: 0.76,
    tempo: 0.95,
    lateRuns: 0.62,
    directness: 0,
    outlet: 0,
  },
  // Balon arriba y que sea lo que Dios quiera.
  patadon: {
    mentality: 0.05,
    pressing: 0.4,
    width: 0.6,
    lineHeight: 0.45,
    lineLength: 0.7,
    compactness: 0.7,
    tempo: 0.34,
    lateRuns: 0.35,
    directness: 1,
    outlet: 0,
  },
  // Esperar y salir como un rayo.
  contra: {
    mentality: -0.15,
    pressing: 0.25,
    width: 0.62,
    lineHeight: 0.38,
    lineLength: 0.76,
    compactness: 0.86,
    tempo: 0.56,
    // Al robar, se suman: la contra es llegar muchos y rapido.
    lateRuns: 0.8,
    directness: 0.7,
    // El 9 no baja: espera la salida.
    outlet: 1,
  },
};

/**
 * Nivel de la IA por division: 0 en la Liga del Barrio (piensa tarde, pasa y
 * chuta mal, elige peor y corre un 13 % menos), 1 en Primera (la IA de
 * siempre). Ver `ai/skill.ts`.
 *
 * Medido con `pnpm barrio:probe` y un "novato" calibrado contra produccion
 * (la gente real perdia 9 de 10 en 3v3 contra la IA de siempre): con el
 * barrio a 0, ese novato gana el 47 %, empata el 13 % y pierde por 3 o mas
 * solo el 14 %. En los modos grandes el nivel pesa poco (manda como juegas
 * tu), asi que la subida es la que se nota: la escalera tambien es de rivales.
 *
 * Desde el 2026-09-29 parte de la torpeza esta en las DECISIONES y no en las
 * piernas (`decisionSlack`). Remedido, el novato en su division: barrio de la
 * carrera 3v3 gana el 52 % (igual que antes), Regional 5v5 el 35 % (antes el
 * 27 %: ver y leer peor pesa mas con mas compañeros, y quita el precipicio
 * que habia del barrio a Regional), Segunda 7v7 el 26 % (antes el 25 %).
 */
export const DIVISION_AI_SKILL: readonly number[] = [0, 0.3, 0.6, 1];

/**
 * El barrio de la CARRERA, un punto mas torpe que el "Barrio" del Partido
 * rapido (que sigue en `DIVISION_AI_SKILL[0]`).
 *
 * Por los estilos de club: desde el 2026-09-28 se notan de verdad (el cerrojo
 * espera, la contra deja a uno arriba…), y el novato del barrio paso de perder
 * por 3 o mas el 14 % al 23 % — sobre todo contra los que esperan, que es donde
 * alguien que empieza conduce hacia un bloque y la pierde. Con el barrio en
 * -0,25 (`pnpm barrio:probe`, `LEVELS=-0.25`): gana 50 %, empata 17 %, pierde
 * 33 %, por 3+ 17 %. Bajarlo a -0,5 no mejora las goleadas (17 %). El Partido
 * rapido no lo necesita: juega con el plan de fabrica, que no cambio.
 */
export const CAREER_BARRIO_SKILL = -0.25;
/** Suelo del nivel en la carrera: el barrio menos el margen del club mas flojo. */
const CAREER_SKILL_FLOOR = CAREER_BARRIO_SKILL - 0.1;

/** El de un club: el de su division, y un poco mas listo cuanto mas fuerte (±0,1). */
export function aiSkillFor(club: Pick<Club, "rating">, tier: number): number {
  const [lo, hi] = TIER_RATING[tier] ?? [0, 1];
  const offset = ((club.rating - (lo + hi) / 2) / Math.max(1, hi - lo)) * 0.2;
  const base = tier === 0 ? CAREER_BARRIO_SKILL : (DIVISION_AI_SKILL[tier] ?? 1);
  return Math.max(CAREER_SKILL_FLOOR, Math.min(1, base + offset));
}

/**
 * Por debajo de esta distancia de color (RGB), dos camisetas se confunden.
 *
 * Medido con el catalogo: a 120 chocaba uno de cada cuatro cruces, y el
 * marron con franja naranja "chocaba" con el rojinegro a listas, que se
 * distinguen de sobra. A 90 solo saltan los parecidos de verdad.
 */
export const KIT_CLASH = 90;
/** La segunda equipacion tiene que estar bien lejos de la del local. */
const SECOND_KIT_MIN = 150;

/** Donde juega cada posicion. En orden de preferencia; la ultima es el recambio. */
const SEAT_LABELS: Record<Position, readonly (readonly string[])[]> = {
  DEL: [["ST"]],
  // En el 3v3 no hay medio: se juega arriba, que es donde se toca balon.
  MED: [["CM"], ["LM", "RM"], ["ST"]],
  DEF: [["CB"], ["LB", "RB"]],
};

/**
 * Tu asiento: el de tu posicion, y en tu banda si hay dos. Zurdo a la
 * izquierda, diestro a la derecha, como toda la vida.
 *
 * Ojo con el lado: las calles (`lane`) NO se reflejan entre equipos, solo la
 * profundidad. Mirando a la derecha (local) tu izquierda es arriba (calle
 * baja); mirando a la izquierda (visitante), es abajo.
 */
export function seatFor(
  formation: readonly SpawnSpec[],
  position: Position,
  foot: Foot,
  side: TeamSide,
): number {
  for (const labels of SEAT_LABELS[position]) {
    const seats = formation
      .map((spec, index) => ({ spec, index }))
      .filter(({ spec }) => spec.role !== "gk" && labels.includes(spec.label ?? ""));
    if (seats.length === 0) continue;
    const leftIsLowLane = side === "home";
    const wantLow = (foot === "left") === leftIsLowLane;
    seats.sort((a, b) => (wantLow ? a.spec.lane - b.spec.lane : b.spec.lane - a.spec.lane));
    return seats[0]!.index;
  }
  // Nunca deberia llegar aqui (todas las formaciones tienen CB y ST).
  return formation.findIndex((s) => s.role !== "gk");
}

/**
 * La segunda equipacion de un club: la de su color secundario, como en la
 * vida real (el Ytuano rojinegro se va de negro). Nunca una de premio: esas
 * no las viste nadie en el mundo, solo se ganan.
 */
export function secondKitFor(kitId: string, againstKitId: string): string {
  const own = KITS[kitId];
  const against = KITS[againstKitId];
  if (!own || !against) return kitId;
  let best: string | null = null;
  let bestScore = Infinity;
  for (const id of KIT_IDS) {
    if (id === kitId || id === againstKitId || isRewardKit(id)) continue;
    const k = KITS[id]!;
    if (colorDistance(k.primary, against.primary) < SECOND_KIT_MIN) continue;
    const score = colorDistance(k.primary, own.secondary);
    if (score < bestScore) {
      bestScore = score;
      best = id;
    }
  }
  return best ?? contrastKitFor(against).id;
}

/** Las dos camisetas; si chocan, se cambia el visitante, como manda el reglamento. */
export function kitsFor(homeKitId: string, awayKitId: string): [string, string] {
  const home = KITS[homeKitId];
  const away = KITS[awayKitId];
  if (!home || !away) return [homeKitId, awayKitId];
  const clash = colorDistance(home.primary, away.primary) < KIT_CLASH;
  return clash ? [homeKitId, secondKitFor(awayKitId, homeKitId)] : [homeKitId, awayKitId];
}

/** El partido que toca, listo para montar. `null` si no hay partido (fin de temporada, retirado). */
export function careerMatchSetup(career: Career, nickname?: string): CareerMatchSetup | null {
  const f = nextFixture(career);
  if (!f) return null;
  const division = DIVISIONS[f.tier]!;
  const modeId = division.modeId;
  const side: TeamSide = f.home ? "home" : "away";
  const homeClub = f.home ? f.club : f.opponent;
  const awayClub = f.home ? f.opponent : f.club;
  const [homeKit, awayKit] = kitsFor(homeClub.kitId, awayClub.kitId);
  const homeColors = KITS[homeKit]!;
  const awayColors = KITS[awayKit]!;
  const player = career.player;
  return {
    season: f.season,
    matchday: f.matchday,
    tier: f.tier,
    modeId,
    side,
    seatIndex: seatFor(MATCH_MODES[modeId].formation, player.position, player.foot, side),
    teams: {
      home: { clubId: homeClub.id, name: homeClub.name, kitId: homeKit, style: homeClub.style },
      away: { clubId: awayClub.id, name: awayClub.name, kitId: awayKit, style: awayClub.style },
    },
    instructions: {
      home: { ...STYLE_INSTRUCTIONS[homeClub.style] },
      away: { ...STYLE_INSTRUCTIONS[awayClub.style] },
    },
    pitchSkin: f.stadium.skinId,
    stadium: {
      crowd: f.stadium.crowd,
      // Mosaico solo donde hay presupuesto para cartulina: Segunda y Primera.
      tifo: f.tier >= 2,
      local: { primary: homeColors.primary, secondary: homeColors.secondary },
      visitor: { primary: awayColors.primary, secondary: awayColors.secondary },
    },
    talent: {
      home: talentFromClubRating(homeClub.rating),
      away: talentFromClubRating(awayClub.rating),
      human: talentFromAttributes(player.attributes),
    },
    // Tu vestuario juega mejor (o peor) contigo segun como os llevais, y un
    // rival al que has picado sale mas afinado. Ver `phoneTalk.ts`.
    aiSkill: (() => {
      const mine = squadSkillOffset(relationsOf(career));
      const provoked =
        career.provoked?.season === f.season && career.provoked.matchday === f.matchday ? PROVOKED_SKILL : 0;
      const clampSkill = (v: number): number => Math.max(CAREER_SKILL_FLOOR, Math.min(1, v));
      return {
        home: clampSkill(aiSkillFor(homeClub, f.tier) + (side === "home" ? mine : provoked)),
        away: clampSkill(aiSkillFor(awayClub, f.tier) + (side === "away" ? mine : provoked)),
      };
    })(),
    seed: hashSeed(career.seed, "match", f.season, f.matchday),
    shirtNumber: player.number,
    nickname: nickname?.trim() || player.name,
  };
}

/** Lo que cuenta la carrera de un partido jugado, sacado de tu ficha del partido. */
export function performanceOf(input: {
  goalsFor: number;
  goalsAgainst: number;
  isMvp: boolean;
  stat: { goals: number; assists: number; shots: number; passes: number; passesOk: number; recoveries: number } | null;
}): MatchPerformance {
  const s = input.stat;
  return {
    result: input.goalsFor > input.goalsAgainst ? "win" : input.goalsFor < input.goalsAgainst ? "loss" : "draw",
    goalsFor: input.goalsFor,
    goalsAgainst: input.goalsAgainst,
    goals: s?.goals ?? 0,
    assists: s?.assists ?? 0,
    mvp: input.isMvp,
    shots: s?.shots ?? 0,
    passes: s?.passes ?? 0,
    passesOk: s?.passesOk ?? 0,
    recoveries: s?.recoveries ?? 0,
  };
}
